import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";

const STEPS = ["Company", "Values", "Cycle", "Users"];

const DEFAULT_VALUES = [
    { key: "customer_obsession", label: "Customer Obsession" },
    { key: "speed", label: "Speed" },
    { key: "owner_mindset", label: "Owner Mindset" },
    { key: "bar_raising", label: "Bar-raising" },
    { key: "trust", label: "Trust" },
];

export default function ProvisionCompanyWizard() {
    const { api } = useAuth();
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [form, setForm] = useState({
        company_name: "", legal_name: "", primary_industry: "", sub_industry: "",
        country: "", employee_count_band: "50_500", founded_year: "", about: "",
        values_dimensions: DEFAULT_VALUES, values_floor_threshold_pct: 50, values_floor_cap: 59,
        cycle_name: "", cycle_start: "", cycle_end: "", sub_org_slicer: "departments",
        admins: [{ full_name: "", email: "", job_title: "" }],
        executives: [],
    });

    const update = (k, v) => setForm({ ...form, [k]: v });

    const submit = async () => {
        setBusy(true); setError(null);
        try {
            const payload = { ...form };
            if (!payload.founded_year) delete payload.founded_year;
            else payload.founded_year = parseInt(payload.founded_year);
            const r = await api.post("/v2/partner/workspaces", payload);
            setSuccess(r.data);
        } catch (e) {
            setError(e.response?.data?.detail || "Provision failed");
        } finally { setBusy(false); }
    };

    if (success) return (
        <div className="max-w-2xl" data-testid="wizard-success">
            <h1 className="text-3xl font-display font-bold text-white">Workspace provisioned</h1>
            <p className="text-sm text-gray-500 mt-1">{success.name} is live in your portfolio.</p>
            <div className="bg-card border border-emerald-500/30 p-5 mt-6">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Temporary credentials</p>
                {success.admins.map(a => (
                    <div key={a.id} className="text-xs font-mono text-white py-1 border-b border-border/20 last:border-0">
                        {a.email} · <span className="text-emerald-300">{a.temp_password}</span>
                    </div>
                ))}
            </div>
            <div className="mt-6 flex gap-3">
                <button onClick={() => nav("/partner")} className="bg-white text-black px-4 py-2 text-sm font-semibold" data-testid="back-to-dashboard-btn">
                    Back to Dashboard
                </button>
                <button onClick={() => nav("/partner/companies/new")} className="border border-border/60 text-white px-4 py-2 text-sm">
                    Provision Another
                </button>
            </div>
        </div>
    );

    return (
        <div className="max-w-3xl" data-testid="provision-wizard">
            <div className="mb-8">
                <h1 className="text-3xl font-display font-bold text-white">Provision New Company</h1>
                <p className="text-sm text-gray-500 mt-1">Add a portfolio company. 4 steps · ~2 minutes.</p>
            </div>

            <div className="flex items-center mb-8">
                {STEPS.map((s, i) => (
                    <React.Fragment key={s}>
                        <div className={`flex items-center gap-2 ${i === step ? "text-white" : i < step ? "text-emerald-300" : "text-gray-600"}`}>
                            <div className={`w-7 h-7 flex items-center justify-center border ${i === step ? "border-white" : i < step ? "border-emerald-500/40 bg-emerald-500/10" : "border-border/40"} text-xs font-mono`}>
                                {i < step ? <Check className="w-3 h-3" /> : i + 1}
                            </div>
                            <span className="text-xs uppercase tracking-wider font-body">{s}</span>
                        </div>
                        {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-3 ${i < step ? "bg-emerald-500/40" : "bg-border/40"}`} />}
                    </React.Fragment>
                ))}
            </div>

            {step === 0 && <Step1 form={form} update={update} />}
            {step === 1 && <Step2 form={form} update={update} />}
            {step === 2 && <Step3 form={form} update={update} />}
            {step === 3 && <Step4 form={form} update={update} />}

            {error && <p className="text-xs text-red-300 mt-4" data-testid="wizard-error">{error}</p>}

            <div className="flex justify-between mt-8 pt-6 border-t border-border/40">
                <button disabled={step === 0} onClick={() => setStep(step - 1)}
                    className="border border-border/60 text-gray-300 px-4 py-2 text-sm disabled:opacity-40 inline-flex items-center gap-2"
                    data-testid="wizard-back-btn">
                    <ChevronLeft className="w-4 h-4" /> Back
                </button>
                {step < STEPS.length - 1 ? (
                    <button onClick={() => setStep(step + 1)}
                        disabled={step === 0 && (!form.company_name || !form.primary_industry)}
                        className="bg-white text-black px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40"
                        data-testid="wizard-next-btn">
                        Next <ChevronRight className="w-4 h-4" />
                    </button>
                ) : (
                    <button onClick={submit} disabled={busy || !form.admins[0]?.email}
                        className="bg-white text-black px-5 py-2 text-sm font-semibold disabled:opacity-40"
                        data-testid="wizard-submit-btn">
                        {busy ? "Provisioning…" : "Provision Workspace"}
                    </button>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs text-gray-500 block mb-1 font-body">{label}</label>
            {children}
        </div>
    );
}

const baseInput = "w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white";

function Step1({ form, update }) {
    return (
        <div className="grid grid-cols-2 gap-4">
            <Field label="Company name *">
                <input className={baseInput} value={form.company_name} onChange={e => update("company_name", e.target.value)} data-testid="wizard-company-name" />
            </Field>
            <Field label="Legal name">
                <input className={baseInput} value={form.legal_name} onChange={e => update("legal_name", e.target.value)} />
            </Field>
            <Field label="Primary industry *">
                <input className={baseInput} value={form.primary_industry} onChange={e => update("primary_industry", e.target.value)} placeholder="e.g., FinTech" data-testid="wizard-industry" />
            </Field>
            <Field label="Sub-industry">
                <input className={baseInput} value={form.sub_industry} onChange={e => update("sub_industry", e.target.value)} />
            </Field>
            <Field label="Country">
                <input className={baseInput} value={form.country} onChange={e => update("country", e.target.value)} />
            </Field>
            <Field label="Employees">
                <select className={baseInput} value={form.employee_count_band} onChange={e => update("employee_count_band", e.target.value)}>
                    {["under_50", "50_500", "500_5000", "5000_plus"].map(s => <option key={s}>{s.replace(/_/g, "–").replace(/-plus/, "+")}</option>)}
                </select>
            </Field>
            <Field label="Founded year">
                <input className={baseInput} value={form.founded_year} onChange={e => update("founded_year", e.target.value)} type="number" />
            </Field>
            <div className="col-span-2">
                <Field label="About / mission">
                    <textarea className={baseInput} rows={3} value={form.about} onChange={e => update("about", e.target.value)} />
                </Field>
            </div>
        </div>
    );
}

function Step2({ form, update }) {
    const dims = form.values_dimensions;
    const setDim = (i, val) => {
        const next = [...dims]; next[i] = { ...next[i], label: val, key: val.toLowerCase().replace(/\s+/g, "_") };
        update("values_dimensions", next);
    };
    return (
        <div className="space-y-4">
            <p className="text-xs text-gray-500 mb-3">Default 5 dimensions (edit as needed). Used for the values floor rule per Realloc methodology.</p>
            <div className="space-y-2">
                {dims.map((d, i) => (
                    <input key={i} className={baseInput} value={d.label} onChange={e => setDim(i, e.target.value)}
                        data-testid={`wizard-value-dim-${i}`} />
                ))}
            </div>
            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-border/30">
                <Field label="Floor threshold %">
                    <input type="number" className={baseInput} value={form.values_floor_threshold_pct}
                        onChange={e => update("values_floor_threshold_pct", parseFloat(e.target.value))} />
                </Field>
                <Field label="Floor cap (max composite)">
                    <input type="number" className={baseInput} value={form.values_floor_cap}
                        onChange={e => update("values_floor_cap", parseInt(e.target.value))} />
                </Field>
            </div>
        </div>
    );
}

function Step3({ form, update }) {
    return (
        <div className="space-y-4">
            <p className="text-xs text-gray-500 mb-3">Optional first cycle. Skip and add later from the workspace if you prefer.</p>
            <Field label="Cycle name">
                <input className={baseInput} value={form.cycle_name} onChange={e => update("cycle_name", e.target.value)}
                    placeholder="Q3 2026 Workforce Diagnostic" data-testid="wizard-cycle-name" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
                <Field label="Start date">
                    <input type="date" className={baseInput} value={form.cycle_start} onChange={e => update("cycle_start", e.target.value)} />
                </Field>
                <Field label="End date">
                    <input type="date" className={baseInput} value={form.cycle_end} onChange={e => update("cycle_end", e.target.value)} />
                </Field>
            </div>
            <Field label="Sub-org slicer">
                <select className={baseInput} value={form.sub_org_slicer} onChange={e => update("sub_org_slicer", e.target.value)}>
                    {["departments", "business_units", "cost_centers"].map(s => <option key={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
            </Field>
        </div>
    );
}

function Step4({ form, update }) {
    const setAdmin = (i, k, v) => {
        const next = [...form.admins]; next[i] = { ...next[i], [k]: v }; update("admins", next);
    };
    const setExec = (i, k, v) => {
        const next = [...form.executives]; next[i] = { ...next[i], [k]: v }; update("executives", next);
    };
    return (
        <div className="space-y-6">
            <div>
                <p className="text-xs text-gray-500 mb-3 uppercase tracking-wider">Workspace admin (≥1 required)</p>
                {form.admins.map((a, i) => (
                    <div key={i} className="grid grid-cols-3 gap-3 mb-2">
                        <input className={baseInput} placeholder="Full name" value={a.full_name} onChange={e => setAdmin(i, "full_name", e.target.value)} data-testid={`wizard-admin-name-${i}`} />
                        <input className={baseInput} placeholder="Email" type="email" value={a.email} onChange={e => setAdmin(i, "email", e.target.value)} data-testid={`wizard-admin-email-${i}`} />
                        <input className={baseInput} placeholder="Job title" value={a.job_title} onChange={e => setAdmin(i, "job_title", e.target.value)} />
                    </div>
                ))}
                <button onClick={() => update("admins", [...form.admins, { full_name: "", email: "", job_title: "" }])}
                    className="text-xs text-gray-400 hover:text-white">+ Add another admin</button>
            </div>
            <div>
                <p className="text-xs text-gray-500 mb-3 uppercase tracking-wider">Executives (optional)</p>
                {form.executives.map((ex, i) => (
                    <div key={i} className="grid grid-cols-3 gap-3 mb-2">
                        <input className={baseInput} placeholder="Full name" value={ex.full_name} onChange={e => setExec(i, "full_name", e.target.value)} />
                        <input className={baseInput} placeholder="Email" type="email" value={ex.email} onChange={e => setExec(i, "email", e.target.value)} />
                        <select className={baseInput} value={ex.persona || ""} onChange={e => setExec(i, "persona", e.target.value)}>
                            <option value="">Persona…</option>
                            <option>ceo</option><option>chro</option><option>cto</option><option>board_member</option>
                        </select>
                    </div>
                ))}
                <button onClick={() => update("executives", [...form.executives, { full_name: "", email: "", persona: "" }])}
                    className="text-xs text-gray-400 hover:text-white" data-testid="wizard-add-exec">+ Add executive</button>
            </div>
        </div>
    );
}
